import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';
import { STATUS_COMERCIAL_LABELS, DECISOES_OVERRIDE_LABELS, TIPO_RELACIONAMENTO_LABELS } from '@/pages/painel/lib/inteligencia';
import { PAPEL_EMPRESA_LABELS } from '@/pages/painel/lib/comercial';

interface Regra {
  id: number;
  alvo: string;
  tipo_alvo: string | null;
  status_comercial: string | null;
  decisao_override: string | null;
  papeis_adicionais: string[] | null;
  prioridade: string | null;
  motivo: string | null;
  estado: string;
}

const PRIORIDADE_LABELS: Record<string, string> = {
  alta: 'Prioridade alta',
  reativar: 'Reativar',
  monitorar: 'Monitorar',
  nutrir: 'Nutrir',
};

export default function RegrasManuais({ password }: { password: string }) {
  const [regras, setRegras] = useState<Regra[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('inteligencia-admin', {
        body: { action: 'regras_listar', password },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as { regras?: Regra[]; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setRegras(parsed?.regras ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar regras.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password]);

  const aguardando = regras.filter((r) => r.estado === 'aguardando_correspondencia');
  const aplicadas = regras.filter((r) => r.estado === 'aplicada');

  return (
    <section className="bg-white rounded-2xl border border-gray-200 p-6">
      <div className="flex items-center gap-2 mb-1">
        <i className="ri-list-check-3 text-[#E8610A]"></i>
        <h3 className="text-sm font-bold text-gray-900">Regras manuais</h3>
      </div>
      <p className="text-xs text-gray-500 mb-5">
        Decisões comerciais definidas por você. Quando um contato/empresa correspondente aparecer (na importação de WhatsApp), a regra é aplicada automaticamente — nunca o contrário.
      </p>

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4">{error}</div>}

      {loading ? (
        <div className="flex justify-center py-10"><div className="w-7 h-7 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <>
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">
              {aguardando.length} aguardando correspondência
            </span>
            {aplicadas.length > 0 && (
              <span className="text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-1">
                {aplicadas.length} aplicadas
              </span>
            )}
          </div>

          {regras.length === 0 ? (
            <p className="text-sm text-gray-400">Nenhuma regra manual cadastrada.</p>
          ) : (
            <div className="space-y-2">
              {regras.map((r) => (
                <div key={r.id} className="flex items-start justify-between gap-3 border border-gray-100 rounded-xl p-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-gray-900">{r.alvo}</p>
                      {r.tipo_alvo && (
                        <span className="text-[10px] font-semibold text-gray-500 bg-gray-100 rounded-full px-2 py-0.5 uppercase">
                          {r.tipo_alvo}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap mt-1">
                      {r.status_comercial && (
                        <span className="text-[11px] font-semibold text-[#E8610A] bg-[#E8610A]/10 rounded-full px-2 py-0.5">
                          {STATUS_COMERCIAL_LABELS[r.status_comercial] ?? r.status_comercial}
                        </span>
                      )}
                      {r.decisao_override && (
                        <span className="text-[11px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
                          {DECISOES_OVERRIDE_LABELS[r.decisao_override] ?? r.decisao_override}
                        </span>
                      )}
                      {r.prioridade && (
                        <span className="text-[11px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5">
                          {PRIORIDADE_LABELS[r.prioridade] ?? r.prioridade}
                        </span>
                      )}
                      {r.papeis_adicionais?.map((p) => (
                        <span key={p} className="text-[11px] font-semibold text-gray-600 bg-gray-100 rounded-full px-2 py-0.5">
                          {PAPEL_EMPRESA_LABELS[p] ?? TIPO_RELACIONAMENTO_LABELS[p] ?? p}
                        </span>
                      ))}
                    </div>
                    {r.motivo && <p className="text-xs text-gray-500 mt-1">{r.motivo}</p>}
                  </div>
                  <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 whitespace-nowrap shrink-0 ${
                    r.estado === 'aplicada' ? 'bg-green-50 text-green-700' : r.estado === 'ignorada' ? 'bg-gray-100 text-gray-500' : 'bg-amber-50 text-amber-700'
                  }`}>
                    {r.estado === 'aplicada' ? 'Aplicada' : r.estado === 'ignorada' ? 'Ignorada' : 'Aguardando'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}